import { clsxMerge } from '@/app/lib/utils/styles';
import type { ComponentPreviewProps } from '@/app/ui/components/molecules/component-preview/component-preview';
import { inter } from '@/app/ui/fonts';
import Image from 'next/image';
import { useCallback, useEffect, useRef, useState } from 'react';
import Frame from 'react-frame-component';
import { ToastProvider } from 'tailframes-components/components/toast';

export const tailwindCSSHead = `<meta charSet='utf-8'></meta>
    <meta name='viewport' content='width=device-width, initial-scale=1'></meta>

    <link rel='preconnect' href='https://fonts.googleapis.com'></link>
    <link rel='preconnect' href='https://fonts.gstatic.com' crossOrigin=''></link>
    <link href='https://fonts.googleapis.com/css2?family=Inter:wght@300;400;500;600;700;800&amp;display=swap' rel='stylesheet'></link>

    <script src="https://cdn.tailwindcss.com" defer></script>
    <script src="https://cdn.tailwindcss.com?plugins=forms" defer></script>`;

const initialIFrameHTML = `
<!doctype html>
<html>
  <head>
    ${tailwindCSSHead}
  </head>
  <body class="${inter.className} antialiased overflow-hidden">
    <div></div>
  </body>
</html>
`.replace(/(\r\n|\n|\r)/gm, ''); // minifies html

interface PreviewProps extends Pick<ComponentPreviewProps, 'children' | 'gap' | 'direction' | 'className' | 'mobilePreviewClassName'> {
  isMobile: boolean;
}

export const Preview = ({ children, isMobile, gap = 6, direction = 'row', className, mobilePreviewClassName }: PreviewProps) => {
  const ref = useRef<HTMLIFrameElement>();
  const [iframeHeight, setIframeHeight] = useState<number>();

  const handleIframeHeight = useCallback(() => {
    setTimeout(() => {
      setIframeHeight(ref.current?.contentWindow?.document?.body?.scrollHeight ?? 0);
    }, 250);
  }, []);

  useEffect(() => {
    handleIframeHeight();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isMobile]);

  return (
    <div
      className={clsxMerge('relative flex flex-grow justify-center overflow-hidden rounded-2xl border border-gray-200 align-middle', {
        'max-h-[calc(100vh-200px)]': isMobile,
      })}
    >
      <div className='absolute inset-y-0 w-full'>
        <Image
          src='/images/preview-bg.webp'
          role='presentation'
          alt=''
          width='0'
          height='0'
          sizes='100vw'
          className='size-full object-cover'
          quality={100}
          loading='eager'
          priority={true}
        />
      </div>
      <div
        className={clsxMerge(
          'z-10 flex min-h-[60px] w-full items-start justify-center overflow-auto rounded-2xl bg-white transition-[width,height,border] ease-in lg:overflow-hidden',
          {
            'rounded-none duration-200 md:w-[375px] md:border-l md:border-r md:border-gray-200 lg:overflow-auto': isMobile,
            'duration-100': !isMobile,
          }
        )}
      >
        {isMobile && (
          <Frame
            ref={ref as never}
            contentDidMount={handleIframeHeight}
            contentDidUpdate={handleIframeHeight}
            initialContent={initialIFrameHTML}
            className='border-none transition-[height] duration-300 ease-out'
            height={iframeHeight}
            width='100%'
          >
            <ToastProvider>
              <div
                className={clsxMerge(
                  `flex flex-col items-center justify-center px-6 py-12 gap-${gap} md:flex-${direction}`,
                  className,
                  mobilePreviewClassName,
                  { 'h-0': !isMobile }
                )}
              >
                {children}
              </div>
            </ToastProvider>
          </Frame>
        )}
        {!isMobile && (
          <div
            className={clsxMerge(`flex w-full flex-col items-center justify-center px-6 py-12 sm:max-lg:flex-wrap gap-${gap} md:flex-${direction}`, className)}
          >
            {children}
          </div>
        )}
      </div>
    </div>
  );
};
